// Where along a feature each LR section or point sits, for the sidebar's bars,
// profiles, tooltips and live cursor. Pure, so the node tests reach it. The hover
// box keeps the LR SDK's value; these give the range it comes from.
import {
  clampSections,
  closeOffsetSections,
} from "../layers/style/util/stepSections.mjs";

/**
 * The axis a feature's sections are drawn on: its length, or the largest offset
 * when there is no length (or a section runs past it). Never 0.
 * @param {number|null} lengthCm
 * @param {{startCm: number, endCm: number|null}[]} sections
 */
export function axisEndCm(lengthCm, sections) {
  const offsets = sections.flatMap((s) =>
    Number.isFinite(s.endCm) ? [s.startCm, s.endCm] : [s.startCm],
  );
  return Math.max(lengthCm ?? 0, ...offsets) || 1;
}

/**
 * Step sections closed onto the axis: bare offsets end at the next start, open
 * ends at the axis end.
 */
export function closedStepSections(sections, lengthCm) {
  return clampSections(
    closeOffsetSections(sections),
    axisEndCm(lengthCm, sections),
  );
}

/**
 * @returns {{value: string, startCm: number, endCm: number}|undefined}
 */
export function stepValueAt(sections, lengthCm, cm) {
  return closedStepSections(sections, lengthCm).find(
    (s) => cm >= s.startCm && cm < s.endCm,
  );
}

/** A linear chain entry that interpolates: a bare offset with a numeric value. */
export const isProfilePoint = (section) =>
  section.endCm === null &&
  section.value.trim() !== "" &&
  Number.isFinite(Number(section.value));

/**
 * A linear chain as points to interpolate, plus bands for sections that do not
 * interpolate (an explicit range, or a value like `multiple`).
 * @param {{startCm: number, endCm: number|null, value: string}[]} sections
 * @param {number|null} lengthCm
 */
export function buildProfile(sections, lengthCm) {
  const end = axisEndCm(lengthCm, sections);
  const points = [];
  const bands = [];
  sections.forEach((s, i) => {
    if (isProfilePoint(s)) {
      points.push({ cm: s.startCm, value: Number(s.value) });
      return;
    }
    const next = sections.slice(i + 1).find((n) => n.startCm > s.startCm);
    bands.push({
      startCm: s.startCm,
      endCm: s.endCm ?? (next ? next.startCm : end),
      value: s.value,
    });
  });
  const values = points.map((p) => p.value);
  return {
    points,
    bands,
    min: Math.min(0, ...values),
    max: Math.max(0, ...values),
    axisEndCm: end,
  };
}

/**
 * @returns {{value: number|string, startCm: number, endCm: number,
 *   interpolated: boolean}|undefined}
 */
export function profileValueAt(profile, cm) {
  const band = profile.bands.find((b) => cm >= b.startCm && cm < b.endCm);
  if (band)
    return {
      value: band.value,
      startCm: band.startCm,
      endCm: band.endCm,
      interpolated: false,
    };
  const { points } = profile;
  if (points.length === 0 || cm < points[0].cm) return undefined;
  const at = points.find((p) => p.cm === cm);
  if (at)
    return { value: at.value, startCm: cm, endCm: cm, interpolated: false };
  const nextIndex = points.findIndex((p) => p.cm > cm);
  if (nextIndex === -1) {
    const last = points[points.length - 1];
    return {
      value: last.value,
      startCm: last.cm,
      endCm: profile.axisEndCm,
      interpolated: false,
    };
  }
  const a = points[nextIndex - 1];
  const b = points[nextIndex];
  return {
    value: a.value + ((b.value - a.value) * (cm - a.cm)) / (b.cm - a.cm),
    startCm: a.cm,
    endCm: b.cm,
    interpolated: true,
  };
}
